import { createContext, FC, PropsWithChildren as Props, useCallback, useContext, useMemo, useState } from 'react';

import { AuthModal } from 'interfaces/auth';

import { EnterCodeStep } from 'components/Login/components/EnterCode/EnterCodeStep';
import { SetUpPassword } from 'components/SetUpPassword/SetUpPassword';

import { IAuthContext, IResetFormData, ISetUpData, modalErrorType } from './types';
import { Login } from '../Login/Login';
import { Registration } from '../Registration/Registration';
import { ResetPassword } from '../ResetPassword/ResetPassword';

const INITIAL_RESET_FORM_DATA: IResetFormData = {
  email: '',
  code: '',
  password: '',
  confirmPassword: '',
};

const authContext = createContext<IAuthContext>({
  resetFormData: INITIAL_RESET_FORM_DATA,
  setUpData: {
    email: '',
    password: '',
  },
  changeSetUpData: () => {},
  isModalOpened: false,
  setResetFormData: () => {},
  setAuthModal: () => {},
  setIsModalOpened: () => {},
  modalError: null,
  setModalError: () => {},
});

export const useAuthModalsContext = () => useContext(authContext);

export const AuthModalsContextProvider: FC<Props> = ({ children }) => {
  const [modal, setModal] = useState<AuthModal>(AuthModal.SIGN_UP);
  const [isModalOpened, setIsModalOpened] = useState(false);
  const [resetFormData, setResetFormData] = useState<IResetFormData>(INITIAL_RESET_FORM_DATA);
  const [setUpData, changeSetUpData] = useState<ISetUpData>({
    email: '',
    password: '',
  });

  const [modalError, setModalError] = useState<modalErrorType>(null);

  const setAuthModal = useCallback((modal: AuthModal) => setModal(modal), []);

  const handleModalClose = () => {
    setIsModalOpened(false);
  };

  const renderContext = {
    [AuthModal.SIGN_UP]: <Registration isModalOpened={isModalOpened} handleModalClose={handleModalClose} />,
    [AuthModal.LOGIN]: <Login isModalOpened={isModalOpened} onModalClose={handleModalClose} />,
    [AuthModal.RECOVER]: <ResetPassword isModalOpened={isModalOpened} onModalClose={handleModalClose} />,
    [AuthModal.SETUP_PASSWORD]: <SetUpPassword isModalOpened={isModalOpened} onModalClose={handleModalClose} />,
    [AuthModal.ENTER_CODE]: <EnterCodeStep onModalClose={handleModalClose} isModalOpened={isModalOpened} />,
  };

  const authContextValue = useMemo(
    () => ({
      resetFormData,
      isModalOpened,
      setResetFormData,
      setAuthModal,
      setIsModalOpened,
      modalError,
      setModalError,
      setUpData,
      changeSetUpData,
    }),
    [
      resetFormData,
      isModalOpened,
      setResetFormData,
      setAuthModal,
      setIsModalOpened,
      modalError,
      setModalError,
      setUpData,
      changeSetUpData,
    ],
  );

  return (
    <authContext.Provider value={authContextValue}>
      {children}
      {renderContext[modal]}
    </authContext.Provider>
  );
};
